import React from 'react';
import { Link } from 'react-router-dom';
import { FaCode, FaCloud, FaNetworkWired, FaDatabase, FaTools, FaPaintBrush, FaCheckCircle, FaArrowRight } from 'react-icons/fa';

const servicesList = [
  {
    id: 'web-dev',
    title: 'Full-Stack Web & API Development',
    icon: <FaCode size={32} color="#45aaf2" />,
    badge: 'Contract Build',
    description: 'Custom, responsive web applications engineered with React, Node.js, Express, and modern RESTful APIs. Focused on fast load times, mobile UI/UX excellence, and secure backend integration.',
    deliverables: [
      'Single Page Applications (React 19)',
      'REST API Design & Node.js Backend',
      'Database Modeling (MongoDB/SQL)',
      'User Authentication & Payment Flows',
      'Cross-Browser & Mobile Optimization'
    ],
    targetClients: 'Startups, Businesses & Individuals seeking modern digital web presence.'
  },
  {
    id: 'cloud-consulting',
    title: 'Cloud Platform & Infrastructure Setup',
    icon: <FaCloud size={32} color="#00d2d3" />,
    badge: 'AWS & Azure',
    description: 'Cloud architecture design, multi-region deployment, Infrastructure as Code (Terraform), and container orchestration (Docker, Kubernetes, Red Hat OpenShift). Includes cost optimization audits.',
    deliverables: [
      'Terraform Infrastructure as Code (IaC)',
      'AWS & Azure Cloud Provisioning',
      'Docker & Kubernetes Containerization',
      'Cost Reduction & Spending Audits',
      'CI/CD Deployment Pipelines'
    ],
    targetClients: 'Companies looking to migrate, automate, or optimize cloud workloads.'
  },
  {
    id: 'network-security',
    title: 'Network Configuration & Hardening',
    icon: <FaNetworkWired size={32} color="#2ecc71" />,
    badge: 'Cisco Certified',
    description: 'End-to-end Cisco network engineering: VLAN segmentation, OSPF dynamic routing, site-to-site IPSec VPN tunnels, firewall ACL policies, and CyberOps threat assessments.',
    deliverables: [
      'Enterprise VLAN & Subnet Design',
      'Cisco Router & Switch Configuration',
      'IPSec & SSL VPN Setup for Remote Access',
      'Firewall Hardening & ACL Rules',
      'Network Security & Threat Audits'
    ],
    targetClients: 'SMEs & Enterprise offices requiring secure, robust IT network connectivity.'
  },
  {
    id: 'data-analytics',
    title: 'Data Analytics & Metric Dashboards',
    icon: <FaDatabase size={32} color="#f8d210" />,
    badge: 'Python & SPSS',
    description: 'Quantitative data processing, statistical research (SPSS), database query optimization, and real-time visualization dashboards built with Python and Grafana.',
    deliverables: [
      'Python Data Wrangling & Cleaning',
      'Grafana Real-Time Dashboard Creation',
      'SQL Database Query Optimization',
      'SPSS Statistical Analysis & Research',
      'Executive Insights & Visual Reports'
    ],
    targetClients: 'Organizations seeking data-driven decision making & automated metric tracking.'
  },
  {
    id: 'it-support',
    title: 'IT Systems & IoT Technical Support',
    icon: <FaTools size={32} color="#9b59b6" />,
    badge: 'Linux & IoT',
    description: 'Linux server administration, Smart IoT system integration (Raspberry Pi), operating system installation, hardware troubleshooting, and technical helpdesk SLA support.',
    deliverables: [
      'Linux Server Admin & Hardening',
      'Smart IoT Hardware & Sensor Integration',
      'OS Installation & Device Provisioning',
      'Helpdesk & Technical Support SLA',
      'Systems Documentation & Manuals'
    ],
    targetClients: 'Businesses needing reliable IT systems management and Smart IoT implementations.'
  },
  {
    id: 'creative-media',
    title: 'Graphics Design & Audio Editing',
    icon: <FaPaintBrush size={32} color="#e74c3c" />,
    badge: 'Creative Tech',
    description: 'Technical precision meets creative digital content. Professional logo design, CorelDraw vector graphics, branding assets, and high-quality sound mixing via Adobe Audition.',
    deliverables: [
      'Brand Logos & Vector Graphics',
      'Digital Art & UI Graphic Assets',
      'Adobe Audition Sound Mixing & Cleanup',
      'Multimedia Content Editing'
    ],
    targetClients: 'Brands & Creators looking for polished visual identity & clean audio production.'
  }
];

const ServicesPage = () => (
  <div style={{ background: '#071325', minHeight: '100vh', color: '#ffffff', padding: '60px 24px' }}>
    <div style={{ maxWidth: '1150px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '50px' }}>
        <span className="badge badge-green" style={{ marginBottom: '12px' }}>
          💼 Services &amp; Contract Consulting
        </span>
        <h1 style={{ fontSize: '2.5rem', color: '#ffffff', margin: '8px 0' }}>
          Technical Contract Services &amp; Advisory
        </h1>
        <p style={{ color: '#94a3b8', maxWidth: '700px', margin: '0 auto', fontSize: '1.05rem' }}>
          High-performance solutions delivered on time and budget. Available for project-based contracts, freelance builds, and technical advisory.
        </p>
      </div>

      {/* Services Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '30px',
        marginBottom: '70px'
      }}>
        {servicesList.map(service => (
          <div key={service.id} className="glass-card" style={{
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            justify: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '14px' }}>
                  {service.icon}
                </div>
                <span className="badge badge-gold" style={{ fontSize: '0.8rem' }}>
                  {service.badge}
                </span>
              </div>

              <h3 style={{ fontSize: '1.4rem', color: '#ffffff', marginBottom: '12px' }}>
                {service.title}
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '20px' }}>
                {service.description}
              </p>

              <h4 style={{ color: '#45aaf2', fontSize: '0.95rem', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Key Deliverables:
              </h4>
              <ul style={{ paddingLeft: '20px', margin: '0 0 20px 0', color: '#cbd5e1', fontSize: '0.9rem' }}>
                {service.deliverables.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: '6px' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div style={{
                background: 'rgba(7, 19, 37, 0.6)',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#94a3b8',
                marginBottom: '20px'
              }}>
                <strong>Ideal For:</strong> {service.targetClients}
              </div>

              <Link
                to="/contact"
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center', fontSize: '0.9rem' }}
              >
                Inquire &amp; Request Quote <FaArrowRight size={12} />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* WORKFLOW / PROCESS SECTION */}
      <div className="glass-card" style={{ padding: '44px 32px', marginBottom: '60px' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <span className="badge badge-gold">Structured Delivery</span>
          <h2 style={{ fontSize: '2rem', color: '#ffffff', marginTop: '10px' }}>
            Contract Engagement Process
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
            A transparent 4-step execution model ensuring quality results and clear communication.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '24px'
        }}>
          <div style={{ background: 'rgba(7, 19, 37, 0.6)', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #45aaf2' }}>
            <div style={{ color: '#45aaf2', fontWeight: '800', fontSize: '1.2rem', marginBottom: '6px' }}>01. Discovery</div>
            <h4 style={{ margin: '0 0 8px 0', color: '#ffffff' }}>Requirements &amp; Scope</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>Deep dive into project needs, architecture goals, timelines, and budget constraints.</p>
          </div>

          <div style={{ background: 'rgba(7, 19, 37, 0.6)', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #00d2d3' }}>
            <div style={{ color: '#00d2d3', fontWeight: '800', fontSize: '1.2rem', marginBottom: '6px' }}>02. Architecture</div>
            <h4 style={{ margin: '0 0 8px 0', color: '#ffffff' }}>Proposal &amp; Design</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>Technical specification, cloud/network architecture blueprint, and milestone schedule.</p>
          </div>

          <div style={{ background: 'rgba(7, 19, 37, 0.6)', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #2ecc71' }}>
            <div style={{ color: '#2ecc71', fontWeight: '800', fontSize: '1.2rem', marginBottom: '6px' }}>03. Execution</div>
            <h4 style={{ margin: '0 0 8px 0', color: '#ffffff' }}>Build &amp; Hardening</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>Agile development, containerization, security testing, and continuous progress updates.</p>
          </div>

          <div style={{ background: 'rgba(7, 19, 37, 0.6)', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #f8d210' }}>
            <div style={{ color: '#f8d210', fontWeight: '800', fontSize: '1.2rem', marginBottom: '6px' }}>04. Handover</div>
            <h4 style={{ margin: '0 0 8px 0', color: '#ffffff' }}>Deploy &amp; Support</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>Production launch, complete technical documentation, team walkthrough, and SLA support.</p>
          </div>
        </div>
      </div>

      {/* CTA BANNER */}
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ fontSize: '1.8rem', color: '#ffffff', marginBottom: '12px' }}>
          Have a Project or Consultation in Mind?
        </h3>
        <p style={{ color: '#94a3b8', fontSize: '1rem', marginBottom: '24px' }}>
          Get in touch today for a free initial scope evaluation and quote.
        </p>
        <Link to="/contact" className="btn-primary">
          <FaCheckCircle /> Book Consultation / Request Quote
        </Link>
      </div>

    </div>
  </div>
);

export default ServicesPage;
